import {SevenElevenExportRequest, SevenElevenExportPreview} from './seven-eleven-export';
import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import * as _ from 'lodash';
import {Observable, map, of} from 'rxjs';
import {REMIT_BASE_URL} from './config';
import {RemitApiResponse} from './kyc/user-verification.service';
import {RemitPublicImageUploaderService} from './remit-public-image-uploader.service';


export interface RemitShopItem {
  id: string
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
  version: string
  name: string
  description: string
  price: number
  capitalPrice?: number
  isLifePrice?: number
  agentPrice?: number
  image: string
  vipDurationDays: number
  isDeleted: boolean
  isHidden?: 'Y' | 'N'
  shippingCost?: number;
  itemType?: string;
  digitalProductDetails?: DigitalProductDetails
  soldOut?: boolean;
  notes?: string
  available?: boolean;
  tags?: string[]
  barcodes?: string[]
  voucherCount?: number
  originalPrice?: number
  requiredFields?: RequiredFields[]
  shortDescription?: string
  sku?: string
  variant?: ShopItemVariant[],
  size?: string;
}

export interface ShopItemVariant {
  name?: string
  sku?: string
  soldOut?: boolean
}

export interface RequiredFields {
  name: string
  label: string
  type: string
  isRequired: boolean
  minimumLength?: number
  maximumLength?: number
}

export interface DigitalProductDetails {
  id?: string | null
  type?: string | null
}

export interface RemitShopItemCreateForm {
  name: string
  description: string
  price: number
  image: string
  vipDurationDays: number
  shippingCost: number;
  itemType: string;
  digitalProductDetails?: DigitalProductDetails | null
  soldOut?: boolean
  tags?: string[]
  voucherCount?: number
  originalPrice?: number;
  shortDescription?: string
  sku?: string
  variant?: ShopItemVariant[]
  notes?: string
  capitalPrice?: number
  isLifePrice?: number,
  agentPrice?: number,
  barcodes?: string[],
  size?: string;
}

interface RemitShopItemCreateWriteModel {
  name: string
  description: string
  price: number
  image: string
  vipDurationDays: number
  shippingCost: number;
  itemType: string;
  digitalProductDetails?: DigitalProductDetails | null
  soldOut?: string
  tags?: string[]
  voucherCount?: number
  originalPrice?: number
  shortDescription?: string
  sku?: string
  variant?: ShopItemVariant[]
  notes?: string
  capitalPrice?: number
  agentPrice?: number
  isLifePrice?: number
  barcodes?: string[]
  size?: string;
}

export interface ShopItemQueryParams {
  pageNumber?: number | null
  pageSize?: number | null
  name?: string | null
  search?: string | null
  itemType?: string | null
  isDeleted?: 'Y' | 'N' | 'ALL' | null
  isHidden?: 'Y' | 'N' | 'ALL' | null
  sku?: string | null
  tags?: string[] | null
  hasVariants?: boolean
}

export interface ShopOrderItem {
  itemId: string;
  itemType?: string;
  bundlingId: string;
  quantity: number;
  orderPrice: number;
  name: string;
  digitalItemDetail?: DigitalItemDetail;
  goldPaymentDetail?: GoldPaymentDetail;
  transactionStatus: string;
  transactionStatusName?: string | null;
  transactionMessage?: string | null;
  sku?: string;
  variantName: string;
  price?: number;
  size?: string;
}

export interface ShopOrderItemUpdate {
  quantity?: number
  price?: number
  variantIndex?: number
}

export interface DigitalItemDetail {
  refId: string;
  destinationNumber: string;
  accountName?: string;
  adminFee?: number;
  amountDue?: number;
  billId?: string;
  notes?: string;
  period?: string;
  totalAmount?: number;
}

export interface Precheckout {
  items?: ShopOrderItem[];
  totalPrice?: number;
  canCheckout?: 'Y' | 'N'
  errorMessage?: string
  errorItemIndex?: number
}

export interface ShopOrder {
  pointCheckout?: {
    operationId: string; redemptionId: string; orderVersion: string;
    points: string; discountMinor: string;
    state: 'pending' | 'committed' | 'return_pending' | 'returned';
  };
  id: string;
  customerId: string;
  agentNumber?: string;
  centralCustomerId?: string;
  itemsView: ShopOrderItem[];
  name: string;
  phoneNumber: string;
  extraPhoneNumbers: string[];
  address: string;
  totalPrice: number;
  note: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  isDeleted: boolean;
  shippedAt?: string;
  shippedBy: string;
  shippingNumber: string;
  paidAt?: string;
  paidBy: string;
  refTradeNo: string;
  paymentCvsCode: string;
  paymentCvsCodeUrl: string;
  expiredAt?: string;
  orderStatusName?: string;
  orderStatus?: string;
  invoicedAt?: string;
  invoiceNumber?: string;
  invoiceMethod?: string;
  invoiceAmount?: number;
  paymentMethodName?: string;
  paymentMethod?: PaymentMethodId;
  shippingProvider?: number;
  shippingProviderName?: string;
  internalDriverId?: string;
  internalDriverName?: string;
  consignmentShopId?: string;
  consignmentShopName?: string;
  shopCode?: string;
  version: string;
  reason?: string;
  canShipAt?: string;
  subTotal?: number;
  fees?: ShopOrderFee[];
  isExported: boolean;
  deliveryType?: number
}

export type PaymentMethodId = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface OrderPaymentMethodOption {
  id: PaymentMethodId;
  name: string;
  current: boolean;
  available: boolean;
  reason: string;
}

export interface OrderPaymentMethodOptionsResponse {
  message: string;
  body: OrderPaymentMethodOption[];
}

export interface UpdateOrderPaymentMethodResponse {
  message: string;
}

export interface RefreshOrderViewResponse {
  message: string;
  body: ShopOrder;
}

export interface ShopOrderFee {
  id: string
  name: string
  amount: number
}

export interface QueryResult<T> {
  body?: T[]
  totalData: number
}

export interface GetResult<T> {
  body: T
  message: string
}

export interface BaseQueryParams {
  pageNumber?: number | null
  pageSize?: number | null
  sortBy?: string | null
  sortDirection?: number | null
  search?: string | null
}

export type ShopOrderQueryParams = BaseQueryParams & {
  orderStatus?: string | null
  isDeleted?: 'Y' | 'N' | null
  shopCode?: string | null
  userId?: string | null
  createdAtStart?: string | null
  createdAtEnd?: string | null
  shippedAtStart?: string | null
  shippedAtEnd?: string | null
  tags?: string[] | null
  itemId?: string | null
  paymentMethod?: number | null
  shippingProvider?: number | null
  internalDriverName?: string | null
  itemName?: string | null
  customerGroupId?: string | null
  agentNumber?: string | null
  consignmentShopName?: string | null
}

export type ShopItemTagQueryParams = BaseQueryParams

export interface ShopItemType {
  id: string
  name: string
}

export interface PrepaidProduct {
  productCode: string;
  productDescription: string;
  productNominal: string;
  productDetails: string;
  productPrice: number;
  productType: string;
  activePeriod: string;
  status: string;
  iconUrl: string;
  productCategory: string;
}

export interface PostpaidProduct {
  productCode: string;
  productType: string;
  productCategory: string;
  status: number;
  productDescription: string;
  productNominal: string;
  productDetails: string;
  productPrice: number;
  activePeriod: string;
  iconUrl: string;
  name: string;
  fee: number;
  commission: number;
}

export type BundlingOrderAction = 'cancelled' |
  'completed' | 'rejected' | 'returned' | 'einvoice' |
  'undo-cancelled' | 'undo-completed' |
  'undo-delete' | 'undo-rejected' | 'undo-shipped' | 'undo-returned';

export type FeatureStatus = 'hide' | 'beta' | 'prod';


export interface ShopOrderItemSummary {
  body: ShopOrderItemSummaryItem[]
  totalData: number
  totalGross: number
  totalNet: number
  totalCapital: number
}

export interface ShopOrderItemSummaryItem {
  itemId: string
  itemName: string
  totalQuantity: string
  notes: number
  capitalPrice: number
  price: number
  sku: string
  totalGross: number
  totalNet: number
}

interface OrderItemSummaryQueryParams {
  pageNumber: number;
  pageSize: number;
  shopCode: string | null;
  orderStatus: string | null;
  paymentMethod?: number | null;
  createdAtStart?: string | null;
  createdAtEnd?: string | null;
  sortBy?: string | null;
  sortDirection?: string | null;
  sku?: string | null;
  itemName: string;
  tags: string[];
}

export interface RemitShopBranchCreateForm {
  name: string
  code: string
  staffIds: string[]
  fapiaoStaffIds: string[]
  defaultRemitPaymentMode: number | null
}
export interface RemitShopBranch {
  id: string
  name: string
  code: string
  staffIds: string[]
  fapiaoStaffIds: string[]
  defaultRemitPaymentMode: number | null
  isHomeShoppingVisible?: boolean | null
  isDeleted: boolean
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
}
export interface ShopBranchQueryParams {
  pageNumber?: number | null
  pageSize?: number | null
  name?: string | null
  code?: string | null
  isDeleted?: boolean | null
  getAllBranch?: boolean | null
  isHomeShoppingVisible?: boolean | null
  defaultRemitPaymentMode?: 0 | 1 | null
  sortBy?: string | null
  sortDirection?: 1 | -1 | null
}


export interface FeatureConfig {
  features: {
    bundlingHome: FeatureStatus;
    pay88Shop: FeatureStatus;
    shopHeimaoPay: FeatureStatus;
    loanDisbursement: FeatureStatus;
    pay88Referrer: FeatureStatus;
    bankReport: FeatureStatus;
  }
  betaUids: string[];
}

export interface ShopFeatureBetaUser {
  uid: string;
  name: string;
}

export interface ShopFeatureConfigResponse extends FeatureConfig {
  betaUsers?: ShopFeatureBetaUser[];
}

export interface CategoryListing {
  categories?: Category[];
}

export interface Category {
  name: string;
  image: string;
  subCategories?: SubCategory[];
}

export interface SubCategory {
  name: string;
  image: string;
  items?: DiscoveryItem[];
}

export interface DiscoveryItem {
  id: string;
}


export interface ShopItemTagForm {
  name?: string
  description?: string
}

export interface ShopItemTag {
  id: string
  name: string
  description?: string
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
}

export interface RemitOrderCreation {
  customerId?: string
  centralCustomerId?: string
  items: Partial<RemitOrderItem>[]
  note?: string
  address?: string
  paymentMethod?: number
  shippingProvider?: number
  shopCode?: string
  name?: string
  arcNumber?: string
  phoneNumber?: string
  extraPhoneNumbers?: string[]
  canShipAt?: string | null
  deliveryType?: number
  shippingCost?: number
  agentNumber?: string
  internalDriverId?: string
  consignmentShopId?: string
}

export interface InternalDriver {
  id: string
  name: string
  createdAt?: string
  createdBy?: string
  updatedAt?: string
  updatedBy?: string
  isDeleted?: boolean
  version?: string
}

export interface ConsignmentShop {
  id: string
  name: string
  createdAt?: string
  createdBy?: string
  updatedAt?: string
  updatedBy?: string
  isDeleted?: boolean
  version?: string
}

export interface RemitOrderItem {
  itemId: string
  quantity: number
  digitalItemDetail?: Map<string, string>
  variantName?: string
  orderPrice?: number
  goldPaymentDetail?: GoldPaymentDetail
}

export interface GoldPaymentDetail {
  orderId: string
  paymentAmount: number
  installmentFee: number
  paymentMethodId: string
}


export interface ShopOrderSummary {
  date: string
  total: number
  count: number
  perPaymentMethod?: ShopOrderPerPayment[]
}

export interface ShopOrderPerPayment {
  id: number
  count: number
  total: number
  name?: string
}


export interface QueryStockItemParams {
  pageNumber?: number | null
  pageSize?: number | null
  shopCode?: string | null
  locationId?: string | null
  name?: string | null
  sku?: string | null
}

export interface StockItemDetails {
  id: string
  name: string
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
  imageUrl: string
  sku: string
  locationName: string
  quantity: number
  shopCode: string
}

export interface SkuGenerateRequest {
  name?: string;
}

export interface ExcelProcessResult {
  totalRows: number;
  successCount: number;
  errorCount: number;
  itemResults: ItemProcessResult[];
  tagResults: TagResult[];
}

export interface ItemProcessResult {
  row: number;
  sku: string;
  name: string;
  success: boolean;
  errorMessage?: string;
  itemId?: string;
}

export interface TagResult {
  name: string;
  id: string;
  created: boolean;
  existed: boolean;
}

export interface BulkCreateItemRequest {
  items: BulkCreateItemData[];
}

export interface BulkCreateItemData {
  sku: string;
  name: string;
  color?: string;
  size?: string;
  description?: string;
  normalPrice: number;
  agentPrice?: number;
  isLifePrice?: number;
  capital?: number;
  tags?: string[];
  barcode?: string;
}

export interface PostpaidReceiptResponse {
  message: string;
  trId: string;
  pdfData: string;
  contentType: string;
  size: number;
}

// --- API Key Management Interfaces ---
export interface ApiKeyCreateRequest {
  name: string;
  description?: string;
}

export interface ApiKeyCreateResponse {
  message: string;
  body: {
    apiKey: string;
    name: string;
    id: string;
  };
}

export interface ApiKeyDetail {
  id: string;
  name: string;
  keyHash: string;
  description: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  expiresAt: string;
  isActive: boolean;
  lastUsedAt: string;
}

export interface ApiKeyDetailResponse {
  message: string;
  body: ApiKeyDetail;
}

export interface ApiKeyQueryParams {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: number;
  merchantId?: string;
}

export interface ApiKeyQueryResponse {
  message: string;
  body: ApiKeyDetail[];
  totalData: number;
}

export interface ApiKeyRevokeResponse {
  message: string;
}

// --- StaffShop Merchant Interfaces ---
export interface Merchant {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  version: string;
}

export interface MerchantCreateRequest {
  name: string;
}

export interface MerchantUpdateRequest {
  name: string;
}

export interface MerchantQueryParams {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: number;
  name?: string;
  id?: string;
}

export interface MerchantQueryResponse {
  message: string;
  body: Merchant[];
  totalData: number;
}

export interface MerchantGetResponse {
  message: string;
  body: Merchant;
}

@Injectable({
  providedIn: 'root',
})
export class RemitShopService {
  constructor(
    private http: HttpClient,
    private publicImageUploader: RemitPublicImageUploaderService,
    @Inject(REMIT_BASE_URL) private remitApiUrl: string,
  ) { }

  createShopItem(data: Partial<RemitShopItemCreateForm>):
    Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/staff/bundling',
        this.buildUpdateWriteModel(data),
    );
  }

  updateShopItem(id: string, data: Partial<RemitShopItemCreateForm>):
    Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + `/staff/bundling/${id}`,
        this.buildUpdateWriteModel(data),
    );
  }

  private buildUpdateWriteModel(data: Partial<RemitShopItemCreateForm>):
    Partial<RemitShopItemCreateWriteModel> {
    const writeModel: Partial<RemitShopItemCreateWriteModel> = {
      name: data.name,
      description: data.description,
      price: data.price,
      image: data.image,
      vipDurationDays: data.vipDurationDays,
      shippingCost: data.shippingCost,
      itemType: data.itemType,
      digitalProductDetails: data.digitalProductDetails,
      tags: data.tags,
      voucherCount: data.voucherCount,
      originalPrice: data.originalPrice,
      shortDescription: data.shortDescription,
      sku: data.sku,
      variant: _.cloneDeep(data.variant),
      notes: data.notes,
      capitalPrice: data.capitalPrice,
      barcodes: data.barcodes,
      isLifePrice: data.isLifePrice,
      agentPrice: data.agentPrice,
      size: data.size,
    };


    if (data.soldOut === true) {
      writeModel.soldOut = 'Y';
    }
    if (data.soldOut === false) {
      writeModel.soldOut = 'N';
    }


    return writeModel;
  }


  getShopItem(id: string): Observable<RemitShopItem> {
    return this.http.get<{ body: RemitShopItem }>(
        this.remitApiUrl + `/staff/bundling/${id}`,
    ).pipe(
        map((v) => v.body),
    );
  }

  getShopItemIdentityAware(params: {
    uid: string, itemId: string,
  }): Observable<RemitShopItem> {
    return this.http.post<{ body: RemitShopItem }>(
        this.remitApiUrl + '/staff/item/identity-aware-get',
        params,
    ).pipe(
        map((v) => v.body),
    );
  }

  queryShopItems(params: ShopItemQueryParams):
    Observable<QueryResult<RemitShopItem>> {
    return this.http.post<QueryResult<RemitShopItem>>(
        this.remitApiUrl + `/staff/bundlings`,
        params,
    );
  }

  deleteShopItem(id: string): Observable<RemitApiResponse> {
    return this.http.delete<RemitApiResponse>(
        this.remitApiUrl + `/staff/bundling/${id}`,
    );
  }

  hideShopItem(id: string): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/bundling/${id}/hide`,
        {},
    );
  }

  unhideShopItem(id: string): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/bundling/${id}/unhide`,
        {},
    );
  }

  undoDeleteShopItem(id: string): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/bundling/${id}/undo-delete`,
        {},
    );
  }

  queryItemStock(params: QueryStockItemParams):
    Observable<QueryResult<StockItemDetails>> {
    return this.http.post<QueryResult<StockItemDetails>>(
        this.remitApiUrl + `/staff/stock-aggregate`,
        params,
    );
  }

  getShopItemTypes(): Observable<ShopItemType[]> {
    return this.http.get<{ body: ShopItemType[] }>(
        this.remitApiUrl + `/staff/item-types`,
    ).pipe(
        map((v) => v.body),
    );
  }

  isBundlingFeatureEnabled(): Observable<boolean> {
    return this.http.get<{ body: { isEnabled: boolean } }>(
        this.remitApiUrl + `/staff/bundling-feature/is-enabled`,
    ).pipe(
        map((v) => v.body.isEnabled),
    );
  }

  setBundlingFeatureOn(): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/bundling-feature/on`, {},
    );
  }

  setBundlingFeatureOff(): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/bundling-feature/off`, {},
    );
  }

  getOrder(id: string): Observable<ShopOrder> {
    return this.http.get<{ body: ShopOrder }>(
        this.remitApiUrl + `/staff/order/${id}`,
    ).pipe(
        map((v) => v.body),
    );
  }

  refreshSingleOrderView(id: string): Observable<RefreshOrderViewResponse> {
    return this.http.post<RefreshOrderViewResponse>(
        this.remitApiUrl +
        `/staff/order/${encodeURIComponent(id)}/populate-order-view`,
        null,
    );
  }

  getOrderPaymentMethods(id: string):
    Observable<OrderPaymentMethodOption[]> {
    return this.http.get<OrderPaymentMethodOptionsResponse>(
        this.remitApiUrl + `/staff/order/${id}/payment-methods`,
    ).pipe(map((response) => response.body));
  }

  updateOrderPaymentMethod(id: string, paymentMethod: PaymentMethodId):
    Observable<UpdateOrderPaymentMethodResponse> {
    return this.http.put<UpdateOrderPaymentMethodResponse>(
        this.remitApiUrl + `/staff/order/${id}/payment-method`,
        {paymentMethod},
    );
  }

  previewSevenElevenExport(request: SevenElevenExportRequest):
    Observable<SevenElevenExportPreview> {
    return this.http.post<SevenElevenExportPreview>(
        this.remitApiUrl + '/staff/orders/export/seven-eleven/preview',
        request,
    );
  }

  queryOrders(params: Partial<ShopOrderQueryParams>):
    Observable<QueryResult<ShopOrder>> {
    return this.http.post<QueryResult<ShopOrder>>(
        this.remitApiUrl + `/staff/orders-view`,
        params,
    );
  }


  queryOrdersSummary(params: ShopOrderQueryParams):
    Observable<QueryResult<ShopOrderSummary>> {
    return this.http.post<QueryResult<ShopOrderSummary>>(
        this.remitApiUrl + `/staff/orders-summary`,
        params,
    );
  }

  queryOrderItemsSummary(params: Partial<OrderItemSummaryQueryParams>):
    Observable<ShopOrderItemSummary> {
    return this.http.post<ShopOrderItemSummary>(
        this.remitApiUrl + `/staff/orders-item-summary`,
        params,
    );
  }

  setOrderShipped(id: string, data: {
    shippingNumber: string,
  }):
    Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/order/${id}/shipped`,
        data,
    );
  }

  queryPrepaidProducts(): Observable<PrepaidProduct[]> {
    return this.http.post<QueryResult<PrepaidProduct>>(
        this.remitApiUrl + `/staff/prepaid-products`,
        {},
    ).pipe(map((v) => v.body ?? []));
  }

  queryPostpaidProducts(): Observable<PostpaidProduct[]> {
    return this.http.post<QueryResult<PostpaidProduct>>(
        this.remitApiUrl + `/staff/postpaid-products`,
        {},
    ).pipe(map((v) => v.body ?? []));
  }

  createOrder(data: Partial<RemitOrderCreation>):
    Observable<{
      body: ShopOrder
      message: string
      id: string
    }> {
    return this.http.post<{
      body: ShopOrder
      message: string
      id: string
    }>(
        this.remitApiUrl + `/staff/order`,
        data,
    );
  }

  precheckout(data: Partial<RemitOrderCreation>):
    Observable<{
      body: Precheckout
      message: string
    }> {
    return this.http.post<{
      body: Precheckout
      message: string
    }>(
        this.remitApiUrl + `/staff/order-preview`,
        data,
    );
  }

  updateOrder(id: string, data: Partial<{
    address: string,
    note: string,
    canShipAt: string,
    shippingProvider: number,
    internalDriverId: string,
    extraPhoneNumbers: string[],
    name: string,
    phoneNumber: string,
    deliveryType: number,
  }>):
    Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + `/staff/order/${id}`,
        data,
    );
  }

  setOrderPaid(id: string): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/order/${id}/paid`,
        {},
    );
  }

  setOrder(id: string, action: BundlingOrderAction, params?: {
    reason?: string
  }):
    Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/order/${id}/${action}`,
        params,
    );
  }

  processPrepaidTransaction(orderId: string): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/order/${orderId}/top-up`,
        {},
    );
  }

  openOrderInvoice(id: string):
    Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/order/${id}/einvoice`,
        {},
    );
  }

  deleteOrder(id: string):
    Observable<RemitApiResponse> {
    return this.http.delete<RemitApiResponse>(
        this.remitApiUrl + `/staff/order/${id}`,
    );
  }

  getFeatureConfig(): Observable<ShopFeatureConfigResponse | undefined> {
    return this.http.get<{ body?: ShopFeatureConfigResponse }>(
        this.remitApiUrl + `/staff/shop-features`,
    ).pipe(map((v) => v.body));
  }

  getFeatureStates(): Observable<{ id: FeatureStatus, name: string }[]> {
    return of([
      {id: 'hide', name: 'Hide - Not shown'},
      {id: 'beta', name: 'Beta - Shown to selected users only'},
      {id: 'prod', name: 'Production - Shown to all'},
    ]);
  }

  setFeatureStatus(data: Partial<FeatureConfig>): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/shop-features`,
        data,
    );
  }

  uploadPublicImage(file: File): Observable<string> {
    return this.publicImageUploader.uploadPublicImage(file);
  }

  updateCategoryListing(data: Partial<CategoryListing>):
    Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/category`,
        data,
    );
  }

  getCategoryListing(): Observable<CategoryListing> {
    return this.http.
        get<{ body: CategoryListing }>(
            this.remitApiUrl + `/staff/categories`)
        .pipe(map((v) => v.body));
  }

  changeDestinationNumber(orderId: string, index: number,
      data: any): Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl +
      `/staff/order/${orderId}/item/${index}` +
      `/destination-number`,
        data,
    );
  }

  changeOrderItem(orderId: string, index: number,
      itemId: string, data: any): Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl +
      `/staff/order/${orderId}/item/${index}` +
      `/change-order-item`,
        {
          itemId: itemId,
          ...data,
        },
    );
  }

  refreshPrepaidTransactionStatus(orderId: string, orderItemIndex: number):
    Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl +
      `/staff/order/${orderId}/item/${orderItemIndex}` +
      `/refresh`,
        {},
    );
  }

  refreshPostpaidTransactionStatus(orderId: string, orderItemIndex: number):
    Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl +
      `/staff/order/${orderId}/item/${orderItemIndex}` +
      `/refresh-postpaid`,
        {},
    );
  }

  prepaidTransactionTrigger(orderId: string, orderItemIndex: number):
    Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl +
      `/staff/order/${orderId}/item/${orderItemIndex}` +
      `/top-up`,
        {},
    );
  }

  postpaidTransactionTrigger(orderId: string, orderItemIndex: number):
    Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl +
      `/staff/order/${orderId}/item/${orderItemIndex}` +
      `/postpaid-top-up`,
        {},
    );
  }

  createNewItemTag(tag: ShopItemTagForm): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/staff/item-tag',
        tag,
    );
  }

  getItemTag(id: string): Observable<ShopItemTag> {
    return this.http.get<{ body: ShopItemTag }>(
        this.remitApiUrl + '/staff/item-tag/' + id,
    ).pipe(map((v) => v.body));
  }

  updateItemTag(id: string, tag: ShopItemTagForm):
    Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + '/staff/item-tag/' + id,
        tag,
    );
  }

  deleteItemTag(id: string): Observable<RemitApiResponse> {
    return this.http.delete<RemitApiResponse>(
        this.remitApiUrl + '/staff/item-tag/' + id,
    );
  }

  queryItemTags(params: ShopItemTagQueryParams):
    Observable<QueryResult<ShopItemTag>> {
    return this.http.post<QueryResult<ShopItemTag>>(
        this.remitApiUrl + '/staff/item-tags',
        params,
    );
  }

  createShopBranch(data: Partial<RemitShopBranch>):
    Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/staff/shop-branch',
        data,
    );
  }

  updateShopBranch(id: string, data: Partial<RemitShopBranch>):
    Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + '/staff/shop-branch/' + id,
        data,
    );
  }
  getShopBranch(id: string): Observable<RemitShopBranch> {
    return this.http.get<{ body: RemitShopBranch }>(
        this.remitApiUrl + '/staff/shop-branch/' + id,
    ).pipe(map((v) => v.body));
  }

  queryShopBranch(params: ShopBranchQueryParams):
    Observable<QueryResult<RemitShopBranch>> {
    return this.http.post<QueryResult<RemitShopBranch>>(
        this.remitApiUrl + '/staff/shop-branches',
        params,
    );
  }
  deleteShopBranch(id: string): Observable<RemitApiResponse> {
    return this.http.delete<RemitApiResponse>(
        this.remitApiUrl + '/staff/shop-branch/' + id,
    );
  }

  setShopBranchHomeShoppingVisible(id: string): Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl +
      `/staff/shop-branch/${id}/home-shopping/visible`,
        null,
    );
  }

  setShopBranchHomeShoppingNotVisible(id: string): Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl +
      `/staff/shop-branch/${id}/home-shopping/not-visible`,
        null,
    );
  }

  refreshOrderView(): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/staff/orders/populate/orders-view',
        null,
    );
  }

  orderStatus(): Observable<Record<string, string>> {
    return of({
      '100': 'Waiting',
      '200': 'Paid/Pending',
      '300': 'Processed',
      '400': 'Completed',
      '500': 'Expired',
      '600': 'Problem',
      '700': 'Cancelled',
      '701': 'Rejected',
      '702': 'Returned',
    });
  }

  remitPaymentMode(): Observable<{ id: number; name: string }[]> {
    return of([
      {id: 0, name: 'Kode Minimarket'},
      {id: 1, name: 'Cash'},
    ]);
  }

  paymentMethod(): Observable<{ id: number; name: string }[]> {
    return this.http.post<{
      body: {PaymentMethod: number; Name: string}[]
    }>(this.remitApiUrl + '/staff/order-payment-methods', null).pipe(
        map((response) => response.body.map((method) => ({
          id: method.PaymentMethod,
          name: method.Name,
        }))),
    );
  }


  shippingProvider(): Observable<{ id: number; name: string }[]> {
    return this.http.post<{
      body: {ShippingProvider: number; Name: string}[]
    }>(this.remitApiUrl + '/staff/order-shipping-providers', null).pipe(
        map((response) => response.body.map((provider) => ({
          id: provider.ShippingProvider,
          name: provider.Name,
        }))),
    );
  }

  queryInternalDrivers(search: string): Observable<QueryResult<InternalDriver>> {
    return this.http.post<QueryResult<InternalDriver>>(
        this.remitApiUrl + `/staff/internal-drivers`,
        {
          search,
          pageNumber: 0,
          pageSize: 10,
          sortBy: 'name',
          sortDirection: 1,
        },
    );
  }

  createInternalDriver(name: string): Observable<{
    message: string
    id: string
    body: InternalDriver
  }> {
    return this.http.post<{
      message: string
      id: string
      body: InternalDriver
    }>(
        this.remitApiUrl + `/staff/internal-driver`,
        {name},
    );
  }

  queryConsignmentShops(search: string): Observable<QueryResult<ConsignmentShop>> {
    return this.http.post<QueryResult<ConsignmentShop>>(
        this.remitApiUrl + `/staff/consignment-shops`,
        {
          search,
          pageNumber: 0,
          pageSize: 10,
          sortBy: 'name',
          sortDirection: 1,
        },
    );
  }

  createConsignmentShop(name: string): Observable<{
    message: string
    id: string
    body: ConsignmentShop
  }> {
    return this.http.post<{
      message: string
      id: string
      body: ConsignmentShop
    }>(
        this.remitApiUrl + `/staff/consignment-shop`,
        {name},
    );
  }

  removeItem(orderId: string, index: number): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/order/${orderId}/remove-item/${index}`,
        {},
    );
  }

  addItem(
      orderId: string,
      items: Partial<RemitOrderItem>[],
  ): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/order/${orderId}/add-item`,
        {items: items},
    );
  }

  updateOrderExport(orderId: string): Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + `/staff/order/${orderId}/export`,
        {},
    );
  }

  editOrderItem(orderId: string, itemIndex: number, updateItem: ShopOrderItemUpdate):
    Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/staff/order/${orderId}/edit-item/${itemIndex}`,
        updateItem,
    );
  }

  generateSku(name?: string): Observable<string> {
    return this.http.post<{ body: string; message: string }>(
        this.remitApiUrl + '/staff/sku/generate',
        {name},
    ).pipe(map((response) => response.body));
  }

  bulkCreateItems(request: BulkCreateItemRequest): Observable<ExcelProcessResult> {
    return this.http.post<{
      message: string;
      results: ExcelProcessResult;
    }>(
        this.remitApiUrl + '/staff/bulk-items',
        request,
    ).pipe(map((response) => response.results));
  }

  downloadPostpaidReceipt(orderId: string, itemId: string):
    Observable<PostpaidReceiptResponse> {
    return this.http.post<PostpaidReceiptResponse>(
        this.remitApiUrl + `/staff/order/${orderId}/item/${itemId}/download-receipt`,
        {},
    );
  }

  /**
   * Create a new API key for the current staff user.
   * @param {ApiKeyCreateRequest} data The API key creation request (name, description)
   * @return {Observable<ApiKeyCreateResponse>} Observable emitting the API key
   *   creation response
   */
  createApiKey(data: ApiKeyCreateRequest): Observable<ApiKeyCreateResponse> {
    return this.http.post<ApiKeyCreateResponse>(
        this.remitApiUrl + '/staff/api-key',
        data,
    );
  }

  /**
   * Get API key details by ID.
   * @param {string} id The API key ID
   * @return {Observable<ApiKeyDetail>} Observable emitting the API key details
   */
  getApiKeyById(id: string): Observable<ApiKeyDetail> {
    return this.http.get<ApiKeyDetailResponse>(
        this.remitApiUrl + `/staff/api-key/${id}`,
    ).pipe(map((res) => res.body));
  }

  /**
   * Query API keys with filters and pagination.
   * @param {ApiKeyQueryParams} params Query parameters (name, isActive, createdBy,
   *   limit, offset)
   * @return {Observable<ApiKeyQueryResponse>} Observable emitting the API key
   *   query response
   */
  queryApiKeys(params: ApiKeyQueryParams): Observable<ApiKeyQueryResponse> {
    return this.http.post<ApiKeyQueryResponse>(
        this.remitApiUrl + '/staff/api-keys',
        params,
    );
  }

  /**
   * Revoke (deactivate) an API key by its ID.
   * @param {string} id The API key ID
   * @return {Observable<ApiKeyRevokeResponse>} Observable emitting the revoke
   *   response
   */
  revokeApiKey(id: string): Observable<ApiKeyRevokeResponse> {
    return this.http.post<ApiKeyRevokeResponse>(
        this.remitApiUrl + `/staff/api-key/${id}/revoke`,
        {},
    );
  }

  // --- StaffShop Merchant Methods ---

  /**
   * Create a new merchant.
   * @param {MerchantCreateRequest} data The merchant creation data
   * @return {Observable<RemitApiResponse>} Observable emitting the creation response
   */
  createMerchant(data: MerchantCreateRequest): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/staff/merchant',
        data,
    );
  }

  /**
   * Get a merchant by ID.
   * @param {string} id The merchant ID
   * @return {Observable<Merchant>} Observable emitting the merchant details
   */
  getMerchant(id: string): Observable<Merchant> {
    return this.http.get<MerchantGetResponse>(
        this.remitApiUrl + `/staff/merchant/${id}`,
    ).pipe(
        map((response) => response.body),
    );
  }

  /**
   * Query merchants with filters and pagination.
   * @param {MerchantQueryParams} params Query parameters for filtering and pagination
   * @return {Observable<MerchantQueryResponse>} Observable emitting the query response
   */
  queryMerchants(params: MerchantQueryParams): Observable<MerchantQueryResponse> {
    return this.http.post<MerchantQueryResponse>(
        this.remitApiUrl + '/staff/merchants',
        params,
    );
  }

  /**
   * Update a merchant by ID.
   * @param {string} id The merchant ID
   * @param {MerchantUpdateRequest} data The merchant update data
   * @return {Observable<RemitApiResponse>} Observable emitting the update response
   */
  updateMerchant(id: string, data: MerchantUpdateRequest): Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + `/staff/merchant/${id}`,
        data,
    );
  }
}
